import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";
import { setCount, setIcon, setPosition } from "../../../../store/slices/ultrasSlice";
import { setCategoryField, updatePromoFields } from "../../../../store/slices/promoFormSlice";
import positionSettingsApi from "../../../../api/positionSettingsApi";
import inventory from "../../../../api/inventoryApi";
import { setPositions as setPositionsInSlice } from "../../../../store/slices/positionsSlice"; // <--- Импортируем экшен из positionsSlice

const IMAGE_BASE_URL = "https://api.duotetra.quest/uploads/gems/";

const generatePositions = (presetsArray, totalAmount, winnersCount, isCrystals = false) => {
    if (!Array.isArray(presetsArray)) return [];

    if (isCrystals) {
        // Кристаллы выдаются штуками: победители и дроп — только целые.
        // Расхождение от округления победителей добираем в последний (массовый) тир,
        // бюджет тира пересчитываем как winners * dropPerWinner, чтобы суммы сходились.
        const winners = presetsArray.map(({ win }) =>
            Math.max(1, Math.round((parseFloat(win) / 100) * winnersCount))
        );
        const sumWinners = winners.reduce((s, w) => s + w, 0);
        winners[winners.length - 1] = Math.max(0, winners[winners.length - 1] + (winnersCount - sumWinners));

        const drops = presetsArray.map(({ budget_percent }, i) => {
            const budget = (parseFloat(budget_percent) / 100) * totalAmount;
            return winners[i] > 0 ? Math.max(1, Math.round(budget / winners[i])) : 0;
        });
        const totalBudget = drops.reduce((s, d, i) => s + d * winners[i], 0);

        return presetsArray.map(({ win }, i) => {
            const budget = drops[i] * winners[i];
            return {
                winPercent: parseFloat(parseFloat(win).toFixed(2)),
                winners: winners[i],
                budgetPercent: totalBudget > 0 ? parseFloat(((budget / totalBudget) * 100).toFixed(2)) : 0,
                budget,
                dropPerWinner: drops[i],
            };
        });
    }

    return presetsArray.map(({ win, budget_percent }) => {
        const winNum = parseFloat(win);
        const budgetPercentNum = parseFloat(budget_percent);
        const winnersFloat = (winNum / 100) * winnersCount;
        const winners = parseFloat(winnersFloat.toFixed(2));
        const budget = parseFloat(((budgetPercentNum / 100) * totalAmount).toFixed(2));
        const dropPerWinner = winners > 0 ? parseFloat((budget / winners).toFixed(4)) : 0;
        return {
            winPercent: parseFloat(winNum.toFixed(2)),
            winners,
            budgetPercent: parseFloat(budgetPercentNum.toFixed(2)),
            budget,
            dropPerWinner,
        };
    });
};

export default function PromoPositions() {
    const dispatch = useDispatch();
    const promoForm = useSelector((state) => state.promoForm);
    const [settings, setSettings] = useState([]); // Это локальный стейт компонента
    const [gemTypes, setGemTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [gemLoading, setGemLoading] = useState (false);

    const isOnlyCrystals = promoForm.promoType === "onlyCrystals";

    const fetchSettings = async () => {
        setLoading(true);
        try {
            // Для onlyCrystals — свои настройки из Position Settings, для остальных — как раньше
            const settingsType = isOnlyCrystals ? "onlyCrystals" : "crystalsAndCrypto";
            const data = await positionSettingsApi.getPositionSettingsByPromoType(settingsType);
            setSettings(data);
            dispatch(setPositionsInSlice(data));
            console.log(data)
        } catch {
            message.error("Не удалось загрузить настройки позиций");
        } finally {
            setLoading(false);
        }
    };

    const fetchGemTypes = async () => {
        setGemLoading(true);
        try {
            const data = await inventory.gemCatalogApi.getAllGems();
            setGemTypes(data);
        } catch {
            message.error("Не удалось загрузить типы гемов");
        } finally {
            setGemLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
        fetchGemTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promoForm.promoType]);

    const categoryHandler = (setting) => {
        // Здесь мы уже работаем с данными из 'setting', которые пришли с сервера.
        // minParticipantsCount и minPrizePlacesCount уже корректны для этого 'setting'.

        // Для onlyCrystals бюджет фиксированный — из defaultBudget настройки
        const budgetTotal = isOnlyCrystals
            ? Number(setting.defaultBudget) || 0
            : promoForm.totalAmount;

        dispatch(
            updatePromoFields({
                participantsCount: setting.minParticipantsCount,
                prizePlacesCount: setting.minPrizePlacesCount,
                ...(isOnlyCrystals ? { totalAmount: budgetTotal } : {}),
            })
        );

        const positionsArr = generatePositions(
            setting.presets,
            budgetTotal,
            setting.minPrizePlacesCount, // Используем minPrizePlacesCount из выбранного setting
            isOnlyCrystals
        );

        dispatch(setIcon(setting.gemId));
        dispatch(setCount(setting.number));
        const posName = setting.name.split(" ")[1];
        dispatch(setPosition(posName));
        dispatch(setCategoryField({ field: "category_name", value: setting.name }));
        dispatch(setCategoryField({ field: "positions", value: positionsArr }));
    };

    if (loading || gemLoading) {
        return (
            <div className="w-full flex items-center justify-center p-4">
                <p>Загрузка...</p>
            </div>
        );
    }

    if (!settings.length || !gemTypes.length) {
        return (
            <div className="p-4">
                <p>Нет доступных категорий</p>
            </div>
        );
    }

    const grouped = settings.reduce((acc, setting) => {
        if (!acc[setting.gemId]) acc[setting.gemId] = [];
        acc[setting.gemId].push(setting);
        return acc;
    }, {});

    const groupedEntries = Object.entries(grouped).sort(([, aSettings], [, bSettings]) => {
        const aMax = Math.max(...aSettings.map((s) => s.presets.length));
        const bMax = Math.max(...bSettings.map((s) => s.presets.length));
        return bMax - aMax;
    });

    return (
        <div className="flex w-full flex-col gap-2 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl">
            <div className="flex w-full flex-row pl-2 pt-2">
                <div className="rounded-lg border border-white/10 bg-white/[0.08] px-2 py-0.5 text-sm font-medium text-white/90">
                    Выберите категорию:
                </div>
            </div>
            <div className="flex flex-row w-full justify-center items-center">
                <div className="flex flex-row gap-6 p-4">
                    {groupedEntries.map(([gemId, settingsForGem]) => {
                        const gem = gemTypes.find((g) => g.id === Number(gemId));
                        const iconUrl = gem?.frontImage || gem?.backImage || "";

                        const sortedSettings = settingsForGem.sort(
                            (a, b) => b.presets.length - a.presets.length
                        );

                        return (
                            <div key={gemId} className="flex flex-col items-center gap-2">
                                <div className="w-[40px] h-[40px] flex items-center justify-center overflow-hidden">
                                    <img
                                        src={iconUrl ? `${IMAGE_BASE_URL}${iconUrl}` : ""}
                                        alt=""
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                {sortedSettings.map((setting) => (
                                    <div
                                        key={setting.id}
                                        onClick={() => categoryHandler(setting)}
                                        className="min-w-[75px] cursor-pointer h-[25px] bg-cat_but border border-border_square rounded text-white text-[12px] flex items-center justify-center font-sans font-medium px-2"
                                    >
                                        {setting.name}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
